import React from "react"
import { useId } from "@reach/auto-id"
import { default as ReactSelect, MenuPlacement } from "react-select"
import { isNotEmpty } from "utils/validation"
import { useTranslation } from "react-i18next"
import { FormFields, FormRule, FormError, FormInputValue } from "models/form"
import {
	handleInternalInputValidation,
	FormFieldFloatingLabel,
	StatesAndHelpersFloatingLabel,
} from "../formUtils"
import { Errors } from "../Errors"

import "./Select.scoped.scss"

export type SelectOption = {
	value: string
	label: string
}

export type SelectProps = FormFields & {
	value: SelectOption | string
	options: SelectOption[]
	defaultValue?: SelectOption
	selectClassname?: string
	menuPlacement?: MenuPlacement
}

export const Select: React.FC<SelectProps> = React.forwardRef(
	(
		{
			// Attributes
			id,
			value,
			options,
			name,
			label = "",
			placeholder = "",
			disabled,
			readonly = false,
			// Styles
			style,
			className = "",
			classNameLabel = "",
			selectClassname = "",
			menuPlacement,
			forceFloating = false,
			// Error management
			required = false,
			customRules,
			showErrors,
			// Handler
			onChange,
			onFocus,
			onBlur,
			onValidate,
		},
		ref: React.Ref<HTMLInputElement>
	) => {
		const INPUT_TYPE = "select"
		const prefix = useId()
		const inputId = id ?? `${prefix}-${name}`
		const errorId = `${inputId}-error`

		const getCorrespondingOption = (value: string) => {
			// FIXME: sometimes the value is stored in the value prop
			// sometimes it is stored in the label prop 😬
			const correspondingValue = options.find((option) => option.value === value)
			const correspondingLabel = options.find((option) => option.label === value)
			return correspondingValue || correspondingLabel
		}

		const getCorrectValue = (): SelectOption => {
			// value could be string (normal react-select-behaviour)
			// value could be SelectOption (if default value provided)
			// value could be null (if no default value provided)
			// Ensure selectedOption is always SelectOption
			return value
				? (value as SelectOption)?.value
					? (value as SelectOption)
					: getCorrespondingOption(value as string)
				: null
		}

		const defaultValue: SelectOption = getCorrectValue()

		// prettier-ignore
		const [selectedOption, setSelectedOption] = React.useState<SelectOption>(defaultValue)
		const [selectIsOpen, setSelectIsOpen] = React.useState<boolean>(false)
		const [errors, setErrors] = React.useState<FormError[]>([])
		const { t } = useTranslation()

		const rules: FormRule[] = React.useMemo(
			() => [
				{
					key: "select-empty",
					check: (value: string) => (required ? isNotEmpty(value) : true),
					message: t("error_mandatory_field", {
						ns: "form",
						fieldName: label,
					}),
				},
				...(customRules || []),
			],
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[required]
		)
		// customRules in dependencies array trigger an infinite loop,
		// because custom rules include a check property defining a new function,
		// preventing useMemo to return a valid shallow-comparison
		// or even useDeepMemo to return a valid deep-comparison

		const validate = React.useCallback(
			(currentValue: SelectOption | string) =>
				handleInternalInputValidation(currentValue, name, rules, setErrors, onValidate),
			[rules, name, onValidate]
		)

		// Validate on mount and on each value change
		React.useEffect(() => {
			validate(value)

			// if the value is externally changed (by the parent or another comp)
			// register the value in the local state to reflect it in the comp
			if (value && selectedOption !== value) {
				const newValue: SelectOption = getCorrectValue()
				setSelectedOption(newValue)
			}

			return () => {
				// On unmounted errors and validation are no longer needed
				setErrors([])
				if (onValidate) onValidate(name, [])
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [value])

		// intermediary method to provide the form's onChange function
		// with the expected value format
		const handleChange = (event: SelectOption) => {
			const inputValue: FormInputValue<string> = {
				target: {
					name: name,
					value: event.value,
					label: event.label,
				},
			}

			setSelectedOption({
				value: event.value,
				label: event.label,
			})
			onChange && onChange(inputValue)
		}

		// When the inner input is focused by a key, there is currently no mean
		// to tell the parent to adjuste css.
		// Expecting :focus-visible-within https://github.com/WICG/focus-visible/issues/151
		const [isFocusVisible, setIsFocusVisible] = React.useState<boolean>(false)
		const isFocusVisibleClassname = isFocusVisible ? "is-focus-visible-fix" : ""

		return (
			<FormFieldFloatingLabel
				inputType={INPUT_TYPE}
				inputValue={selectedOption}
				forceFloating={forceFloating}
			>
				{({
					floatingLabelClassname,
					onFloatingLabelFocus,
					onFloatingLabelBlur,
				}: StatesAndHelpersFloatingLabel) => (
					<div
						className={`c-form-field ${className} ${floatingLabelClassname}`}
						style={{
							...style,
							// To prevent overlapping consecutive select, the z-index is
							// dynamically adjusted. An open select will have a bigger
							// z-index to ensure it is on the top.
							zIndex: `var(${
								selectIsOpen ? "--zFormFieldOpenSelect" : "--zFormFieldForm"
							})` as unknown as number,
						}}
					>
						<label
							className={`c-form-field__label c-label px-2 ${classNameLabel}`}
							htmlFor={inputId}
						>
							{label} {required && "*"}
						</label>

						<ReactSelect
							options={options}
							value={selectedOption}
							inputId={inputId}
							rer={ref}
							name={name}
							isDisabled={disabled || readonly}
							placeholder={placeholder}
							noOptionsMessage={() => t("no_result", { ns: "form" })}
							className={`c-form-field__form c-select px-5 ${isFocusVisibleClassname} ${
								selectClassname ? selectClassname : ""
							}`}
							classNamePrefix={"c-select"}
							menuPlacement={menuPlacement}
							onMenuOpen={() => setSelectIsOpen(true)}
							onMenuClose={() => setSelectIsOpen(false)}
							onChange={(event) => {
								!readonly && onChange && handleChange(event)
							}}
							onFocus={(event) => {
								onFloatingLabelFocus()
								setIsFocusVisible(true)
								validate && validate(selectedOption?.value)
								onFocus && onFocus(event)
							}}
							onBlur={(event) => {
								onFloatingLabelBlur()
								setIsFocusVisible(false)
								validate && validate(selectedOption?.value)
								onBlur && onBlur(event)
							}}
							aria-describedby={errorId}
							aria-invalid={Boolean(errors.length)}
							aria-live="polite"
						/>

						{/* Error messages */}
						{showErrors && <Errors id={errorId} errors={errors} />}
					</div>
				)}
			</FormFieldFloatingLabel>
		)
	}
)
